import React from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { SpecsSelectField } from "./SpecsEditorFields/index";
import type { PartModel } from "./specsModel/PartModel";
import { useSpecsEditorContextValue } from "./useSpecsEditor";

type SpecsInheritanceGroupDialogProps = {
  open: boolean;
  part: PartModel;
  value: string;
  onChange: (value: string) => void;
  onClose: () => void;
  onCreate: () => void;
};

function formatInheritanceGroupName(partName: string, layerName: string) {
  if (!partName || !layerName) {
    return "";
  }

  return `${partName}${layerName.charAt(0).toUpperCase()}${layerName.slice(1)}`;
}

export function getSuggestedInheritanceGroups(part: PartModel) {
  const mainElement = part.getMainElement();
  const groups =
    mainElement?.styles.flatMap((style) =>
      style.layers.map((layer) =>
        formatInheritanceGroupName(part.name, layer.name)
      )
    ) ?? [];

  return Array.from(
    new Set(groups.filter((groupName): groupName is string => !!groupName))
  ).sort();
}

export default function SpecsInheritanceGroupDialog({
  open,
  part,
  value,
  onChange,
  onClose,
  onCreate,
}: SpecsInheritanceGroupDialogProps) {
  const { currentVersion } = useSpecsEditorContextValue();
  const existingGroups = currentVersion?.getInheritanceGroups(part.name) ?? [];
  const suggestedGroups = getSuggestedInheritanceGroups(part);
  const options = suggestedGroups.map((groupName) => ({
    value: groupName,
    label: existingGroups.includes(groupName)
      ? `${groupName} (existing)`
      : groupName,
  }));

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle sx={{ pb: 1 }}>Create Inheritance Group</DialogTitle>
      <DialogContent sx={{ minWidth: 320 }}>
        {options.length > 0 ? (
          <SpecsSelectField
            label="Group name"
            value={value}
            options={options}
            onChange={onChange}
          />
        ) : (
          <Typography variant="body2" color="text.secondary">
            No layer-based group names are available for this part.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annuler</Button>
        <Button variant="contained" disabled={!value} onClick={onCreate}>
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
}
